import { FilterList } from '@mui/icons-material';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { translateKey } from 'src/locales/translate';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import { visitApi } from 'src/api/visit';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import ProfileVisitHistoryFilters, {
  EMPTY_HISTORY_FILTERS,
  ProfileHistoryFilters,
  toHistoryQuery,
} from 'src/sections/@dashboard/visit/components/ProfileVisitHistoryFilters';
import ProfileVisitsHistoryTable from 'src/sections/@dashboard/visit/components/ProfileVisitsHistoryTable';
import ProfileVisitsHistoryTableSkeleton from 'src/sections/@dashboard/visit/components/ProfileVisitsHistoryTableSkeleton';
import {
  CARD_HEADER_SX,
  CARD_HEADER_TITLE_PROPS,
} from 'src/theme/cardHeader';
import { useTable } from 'src/components/table';
import { TAB_TABLE_DEFAULT_ROWS_PER_PAGE } from 'src/theme/tableContainer';

export const OrganizationPatientVisitsHistoryContent = () => {
  const { patientId, visitId } = useParams<{
    patientId: string;
    visitId?: string;
  }>();
  const {
    isProfessionalRoleSelected,
    isOrganizationAdminRoleSelected,
    selectedOrganization,
  } = useOrganizationContext();

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: TAB_TABLE_DEFAULT_ROWS_PER_PAGE,
  });

  /**
   * Inside a visit the professional is working, and what they want is what
   * actually happened — so the card opens on finished visits only. On the
   * patient's own page it opens on the whole history, which is what that page
   * is for. The route's `visitId` is the same discriminator the Zabiegi row
   * selection uses.
   */
  const [filters, setFilters] = useState<ProfileHistoryFilters>(() =>
    visitId
      ? { ...EMPTY_HISTORY_FILTERS, statuses: ['COMPLETED'] }
      : EMPTY_HISTORY_FILTERS,
  );
  const [filtersOpen, setFiltersOpen] = useState(false);

  const query = toHistoryQuery(filters);

  const { data, isLoading } = useQuery(
    [
      'patient-visit-history',
      patientId,
      selectedOrganization?.id,
      page,
      rowsPerPage,
      query,
    ],
    () =>
      visitApi.visitControllerGetProfileVisitHistoryPreview({
        profileId: patientId!,
        page,
        limit: rowsPerPage,
        ...query,
      }),
    {
      retry: 1,
      enabled:
        (isProfessionalRoleSelected || isOrganizationAdminRoleSelected) &&
        !!selectedOrganization?.id &&
        !!patientId,
      // Keep the current page on screen while the next one loads.
      keepPreviousData: true,
    },
  );

  const handleFiltersChange = (next: ProfileHistoryFilters) => {
    setFilters(next);
    // A narrower list has fewer pages; staying on page 3 of the old one would
    // show an empty table.
    onChangePage(null, 0);
  };

  const hasFilters =
    Boolean(filters.startDate || filters.endDate) ||
    Boolean(filters.closedFrom || filters.closedTo) ||
    Boolean(filters.professionalName?.trim()) ||
    filters.statuses.length > 0 ||
    filters.noteStatuses.length > 0;

  return !isLoading ? (
    <>
      <Stack sx={CARD_HEADER_SX}>
        <Typography {...CARD_HEADER_TITLE_PROPS}>
          {translateKey('search.book.promoHistoryTitle')}
        </Typography>
        {/* Labelled, not just an icon: people using this said they could not
            tell there were filters here at all. */}
        <Button
          size="small"
          variant="outlined"
          startIcon={<FilterList />}
          data-testid="history-filters-toggle"
          onClick={() => setFiltersOpen((open) => !open)}
          color={filtersOpen || hasFilters ? 'primary' : 'inherit'}
        >
          {translateKey('visit.historyFiltersTooltip')}
        </Button>
      </Stack>
      <ProfileVisitHistoryFilters
        value={filters}
        onChange={handleFiltersChange}
        isOpen={filtersOpen}
        showProfessionalFilter
      />
      <ProfileVisitsHistoryTable
        data={data?.data.data || []}
        page={page}
        rowsPerPage={rowsPerPage}
        total={data?.data.total ?? 0}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        emptyMessage={
          hasFilters ? translateKey('visit.historyFilterEmpty') : undefined
        }
      />
    </>
  ) : (
    <>
      <Stack sx={CARD_HEADER_SX}>
        <Skeleton variant="rounded" height="24px" width="104px" />
      </Stack>
      <ProfileVisitsHistoryTableSkeleton />
    </>
  );
};
